import { Injectable, Res, UnauthorizedException } from '@nestjs/common';
import { JwtService } from '@nestjs/jwt';
import * as bcrypt from 'bcrypt';
import type { Response } from 'express';
import { DataSource } from 'typeorm';

import { AuthenticationReq, CommonResponse, ForgotPasswordReq, GlobalResponseObject, ResetPasswordReq } from '@boi/shared-models';
import { ConfigService } from '@nestjs/config';
import * as crypto from 'crypto';
import { GenericTransactionManager } from '../../database/typeorm-transactions';
import { AuthenticationAdapter } from './decorators/authentication-adapter';
import { AuthenticationsDto } from './dtos/authentications.dto';
import { JwtPayload } from './dtos/jwt.payload';
import { LoginUserDto } from './dtos/user-login.dto';
import { AuthenticationEntity } from './entities';
import { AuthenticationRepository } from './repositories/authentication.repository';

@Injectable()
export class AuthenticationsService {
  private readonly dbNames: any;

  constructor(
    private readonly dataSource: DataSource,
    private readonly repository: AuthenticationRepository,
    private readonly jwtService: JwtService,
    private readonly adapter: AuthenticationAdapter,
    private configService: ConfigService,
    // private whatsService: WhatsUpService,
  ) {
    this.dbNames = this.configService.get('dbNames');

  }

  async createAuthentication(req: AuthenticationReq): Promise<CommonResponse> {
    // console.log(req, "r----------")
    const transactionalEntityManager = new GenericTransactionManager(this.dataSource);
    try {
      await transactionalEntityManager.startTransaction();
      const userNameVerification = await transactionalEntityManager.getRepository(AuthenticationEntity).count({ where: { userName: req.username } });
      if (userNameVerification) {
        return new CommonResponse(false, 54650, 'Oops! This Mobile Number Is Already Registered. Please Use A Different Number..');
      } else {
        const entity = new AuthenticationEntity();
        entity.subject = req.subject
        entity.userName = req.username
        entity.password = req.password;
        entity.ogPass = await this.wrapPassword(req.password);
        const saveData = await transactionalEntityManager.getRepository(AuthenticationEntity).save(entity);
        if (saveData) {
          await transactionalEntityManager.completeTransaction();
          return new CommonResponse(true, 11111, 'Account created successfully!', saveData);
        } else {
          await transactionalEntityManager.completeTransaction();
          return new CommonResponse(false, 54650, 'Error in account creation try again!', saveData);
        }
      }
    } catch (err) {
      console.error('Error saving payroll attendance data:', err);
      await transactionalEntityManager.releaseTransaction();
      throw err;
    }
  }

  async UpdateAuthentication(req: AuthenticationReq): Promise<CommonResponse> {
    // console.log(req, "r------")
    const user = await this.repository.findOne({ where: { userName: req.username } });
    if (!user) {
      return new CommonResponse(false, 1111, 'User with this email does not exist.');
    }
    user.password = req.password;
    user.ogPass = await this.wrapPassword(req.password);
    user.sentOtp = null;
    user.otpSentTime = null;
    user.otpExpiryTime = null;
    await this.repository.save(user);
    return new CommonResponse(true, 66666, 'Password has been updated successfully');
  }

  async getAllUsers(req: any): Promise<CommonResponse> {
    try {
      let getData;
      if (req.subject === null) {
        getData = await this.repository.find();
      } else {
        getData = await this.repository.find({ where: { subject: req.subject } })
      }
      if (getData.length > 0) {
        return new CommonResponse(true, 11111, "All Users Got Successfully!", getData);
      } else {
        return new CommonResponse(false, 54650, "No data found!", []);
      }
    } catch (err) {
      return new CommonResponse(false, 54650, "Error in data retrieving!!!");
    }
  }

  async login(req: any, @Res() response: Response): Promise<CommonResponse> {
    // console.log(req,"rr------")
    const userData = await this.repository.findOne({ where: { userName: req.user.userName } });
    try {
      if (!userData) {
        return new CommonResponse(false, 5498, `Oops! We couldn't find your account. Please register to create a new account..`);
      }
      if (userData.noOfFailedLogin && this.configService.get('rateLimiting').maxLoginAttempts === userData.noOfFailedLogin) {
        return new CommonResponse(false, 8985, `Access limit exceeded. Please try again after one hour`);
      }
      const authId = userData.id
      const subject = req.user.subject
      const userName = userData.userName
      const userPayload: JwtPayload = {
        id: req.user.id,
        fullName: req.user.fullName,
        email: req.body.email,
        phone: '',
        externalRefNo: req.user.externalRefNo,
        subject: req.user.subject
      };
      const accessToken = this.generateAccessToken(userPayload);
      const refreshToken = this.generateRefreshToken(userPayload);

      const secretData = {
        accessToken,
        refreshToken,
        authId,
        subject,
        userName
      };
      await this.setCurrentRefreshToken(refreshToken, req.body.usersId);
      if (
        this.configService.get('jwtConfig').jwtByCookieOrHeader === 'cookie'
      ) {
        response.cookie('tokens-data', secretData, {
          httpOnly: true,
          expires: new Date(new Date().getTime() + 86409000),
        });
      }
      const responseObj = { ...secretData };
      response.send(new CommonResponse(true, 5454, "Welcome back! You're now logged in", responseObj));
      return new CommonResponse(true, 9665, "`Welcome back! You're now logged in`", responseObj);
    } catch (error) {
      if (userData) {
        let otherFields = {};
        if (
          userData.noOfFailedLogin ==
          this.configService.get('rateLimiting').maxLoginAttempts - 1
        ) {
          otherFields['accountLockedOn'] = () => 'CURRENT_TIMESTAMP(6)';
        }
        await this.repository.update(
          { userName: req.user.username },
          { noOfFailedLogin: () => 'noOfFailedLogin + 1', ...otherFields }
        );
      }
    }
  }

  // @LogActions({ isAsync: true })
  async changePassword(
    username: string,
    oldPassword: string,
    newPassword: string
  ): Promise<any> {
    const user = await this.repository.findOne({
      where: { userName: username },
    });
    if (!user) {
      throw new UnauthorizedException('User not found !');
    }
    const hashedPassword = await bcrypt.hash(oldPassword, user.salt);
    const isPasswordValid = hashedPassword == user.password;
    if (!isPasswordValid)
      throw new UnauthorizedException('Invalid credentials');
    const entity = new AuthenticationEntity();
    entity.userName = username;
    entity.createdUser = user.createdUser;
    entity.password = newPassword;
    // entity.userName = username;
    if (user.id) {
      entity.id = user.id;
      entity.updatedUser = username;
    }
    const saveData = await this.repository.save(entity);
    return new CommonResponse(true, 66666, 'Password Changed Successfully');
  }

  async getSalt(username: string): Promise<CommonResponse> {
    const user = await this.repository.findOne({ where: { userName: username } });
    if (!user) {
      return new GlobalResponseObject(false, 132, 'User Not Found');
    }
    return new CommonResponse(true, 66666, 'User Was Updated Successfully');
  }

  async validateUser(username: string, password: string): Promise<any> {
    const user = await this.repository.findOne({
      where: { userName: username },
    });
    if (!user) {
      throw new UnauthorizedException('User not found !');
    }

    const hashedPassword = await bcrypt.hash(password, user.salt);
    const isPasswordValid = hashedPassword == user.password;
    if (!isPasswordValid)
      throw new UnauthorizedException('Invalid credentials');
    let externalRefNo = JSON.parse(JSON.stringify(user))?.user?.externalRefNo
      ? JSON.parse(JSON.stringify(user))?.user?.externalRefNo
      : '';
    let usersId = JSON.parse(JSON.stringify(user))?.user?.id
      ? JSON.parse(JSON.stringify(user))?.user?.id
      : '';
    delete user.password;
    delete user.salt;
    delete user.hashedRefreshToken;
    // delete user.user;
    return { ...user, usersId, externalRefNo };
  }

  async getUserIfRefreshTokenMatches(refreshToken: string, username: string) {
    const user = await this.repository.findOne({ where: { userName: username } });
    if (!user) return null;
    const isRefreshTokenMatching = await bcrypt.compare(
      refreshToken,
      user.hashedRefreshToken
    );
    if (isRefreshTokenMatching) {
      delete user.password;
      delete user.salt;
      delete user.hashedRefreshToken;
      return { ...user };
    }
    return null;
  }

  async logOut(
    loginUserDto: LoginUserDto,
    @Res() response: Response
  ): Promise<any> {
    await this.repository.update(
      { userName: loginUserDto.username },
      {
        hashedRefreshToken: null,
      }
    );
    response.clearCookie('tokens-data');
    return response.send(
      new CommonResponse(
        true,
        66666,
        'Authentication Has Been Successfully Done'
      )
    );
  }

  generateAccessToken(payload: JwtPayload) {
    const token = this.jwtService.sign(payload, {
      secret: this.configService.get('jwtConfig').jwtSecret,
      expiresIn: `${this.configService.get('jwtConfig').jwtExpiryTime}`,
    });
    return token;
  }

  generateRefreshToken(payload: JwtPayload) {
    const token = this.jwtService.sign(payload, {
      secret: this.configService.get('jwtConfig').refreshSecret,
      expiresIn: `${this.configService.get('jwtConfig').refreshExpiryTime}`,
    });
    return token;
  }

  async setCurrentRefreshToken(refreshToken: string, userId: number) {
    const hashedRefreshToken = await bcrypt.hash(refreshToken, 10);
    await this.repository.update(
      { id: userId },
      {
        hashedRefreshToken,
      }
    );
  }

  async refreshJwtAccessToken(
    req: any,
    @Res() response: Response
  ): Promise<any> {
    const { user } = req;
    const userData: JwtPayload = {
      id: user.usersId,
      fullName: user.fullName,
      email: user.email,
      phone: '',
      externalRefNo: user.externalRefNo,
      subject: user.subject,
    };
    const accessToken = this.generateAccessToken(userData);
    const refreshToken = req?.cookies['tokens-data'].refreshToken;
    const secretData = {
      accessToken,
      refreshToken,
    };
    response.cookie('tokens-data', secretData, {
      httpOnly: true,
      expires: new Date(new Date().getTime() + 86409000),
    });
    return response.send(
      new CommonResponse(
        true,
        66666,
        'Authentication has been Successfully done',
        secretData
      )
    );
  }

  async wrapPassword(password: string): Promise<any> {
    return `$2b$0$1%${password}$2b$0$1%`;
  }

  // @LogActions({ isAsync: true })
  async sendForgotPasswordOtp(req: ForgotPasswordReq): Promise<CommonResponse> {
    const user = await this.repository.findOne({ where: { userName: req.userName } });

    if (!user) {
      return new CommonResponse(false, 5498, `Oops! We couldn't find your account. Please register to create a new account..`);
    }

    // if (user.otpExpiryTime && new Date(user.otpExpiryTime) > new Date()) {
    //   return new CommonResponse(false, 1111, 'An OTP was already sent and is still valid. Please wait for it to expire.');
    // }

    // Generate a random OTP and its expiry time
    let otp = crypto.randomInt(100000, 999999).toString(); // 6-digit OTP
    const otpExpiryTime = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes from now

    // Save OTP details in the database
    // user.sentOtp = otp;
    // user.otpSentTime = new Date().toISOString();
    // user.otpExpiryTime = otpExpiryTime.toISOString();
    await this.repository.update({ userName: req.userName }, { sentOtp: otp });


    // const mobileNumber = mobile.data[0].mobile;
    const encodedMessage = encodeURIComponent(`OTP for reset password for Sakku Anna Seva donations login is ${otp} THANKS FOR YOUR SUPPORT`);
    //  const smsUrl = `https://api.smscountry.com/SMSCwebservice_bulk.aspx?User=vrplsms&passwd=Sakkuvrpl@123&mobilenumber=${mobileNumber}&message=${encodedMessage}&sid=VRPPLH&mtype=N&DR=Y`;


    try {
      //  const smsResponse = await axios.get(smsUrl);
      // console.log('SMS Sent:', smsResponse.data)
      return new CommonResponse(true, 200, 'OTP sent successfully.', otp);
    } catch (error) {
      console.error('SMS sending failed:', error.message);
      return new CommonResponse(false, 50001, 'Failed to send OTP.');
    }
  }

  async resetPassword(req: ResetPasswordReq): Promise<CommonResponse> {
    const user = await this.repository.findOne({ where: { userName: req.userName } });
    if (!user) {
      return new CommonResponse(false, 1111, 'User with this email does not exist.');
    }

    // console.log(user, req, '-------ssstttteeeppppp----------')
    // Update user's password and clear OTP details
    user.password = req.password;
    user.sentOtp = null;
    user.otpSentTime = null;
    user.otpExpiryTime = null;
    await this.repository.save(user);
    return new CommonResponse(true, 66666, 'Password has been updated successfully');
  }

  async updateAuthentication(req: AuthenticationsDto, transactionalEntityManager: GenericTransactionManager) {
    const entity = new AuthenticationEntity();
    entity.createdUser = req.createdUser;
    entity.password = req.password;
    entity.userName = req.username;
    entity.id = req.authenticationId;
    entity.updatedUser = req.createdUser;
    entity.versionFlag = 1
    const updateData = await transactionalEntityManager.getRepository(AuthenticationEntity).save(entity);
    return updateData;
  }


  async resetPasswordAuthentication(req: AuthenticationsDto, transactionalEntityManager: GenericTransactionManager) {
    const user = await this.repository.findOne({ where: { userName: req.username }, relations: ['user'] });
    const entity = new AuthenticationEntity();
    entity.password = '123456';
    entity.userName = user.userName;
    entity.id = user.id;
    entity.versionFlag = 1
    const updateData = await transactionalEntityManager.getRepository(AuthenticationEntity).save(entity);
    return updateData;
  }

  async checkPassword(req: { username: string, password: string }): Promise<any> {
    const user = await this.repository.findOne({ where: { userName: req.username }, relations: ['user'] });
    if (!user) {
      return new CommonResponse(false, 11111, `Oops! We couldn't find your account. Please register to create a new account..`);
    }
    // const Q = `SELECT is_active AS isActive FROM ${this.dbNames.ems}.employee WHERE employee_code = '${user.user.employeeCode}'`
    // const isActive = await this.repository.query(Q)
    const hashedPassword = await bcrypt.hash(req.password, user.salt);
    const isPasswordValid = hashedPassword == user.password;

    const data = {
      isPasswordValid: isPasswordValid ? true : false,
      versionFlag: user.versionFlag,
      // isActive: isActive[0].isActive
    }
    return new CommonResponse(true, 66666, 'Success', data);
  }


  async updatePassword(req: { username: string, password: string }) {
    const user = await this.repository.findOne({ where: { userName: req.username }, relations: ['user'] });
    const entity = new AuthenticationEntity();
    entity.userName = user.userName;
    entity.createdUser = user.createdUser;
    entity.password = req.password;
    entity.userName = user.userName;
    entity.id = user.id;
    entity.updatedUser = req.username;
    entity.versionFlag = user.versionFlag + 1
    const updateData = await this.repository.save(entity);
    if (updateData) {
      return new CommonResponse(true, 66666, 'Password has been updated successfully');
    } else {
      return new CommonResponse(false, 11111, 'Failed to updated password');
    }
  }


  async checkPhoneNoAlreadyExist(req: { userName: string }) {
    const user = await this.repository.findOne({ where: { userName: req.userName } });

    if (user) {
      return new CommonResponse(true, 66666, 'Account already exists with this phone number');
    } else {
      return new CommonResponse(false, 11111, 'No account found');
    }
  }
}
